import React, { useState, useRef, useLayoutEffect } from "react";
import { SvgIcon } from "@montana/shared/svg-icon";
import { ISharedFieldProperties } from "../field/field";
import { isBrowser } from "@montana/helpers/browser.helper";


interface ICheckable extends
    Pick<React.InputHTMLAttributes<HTMLInputElement>, "name" | "value" | "id" | "required">,
    Omit<ISharedFieldProperties, "value">
{
    className?: string;
    compact?: boolean;
    disabled?: boolean;
}

export interface IRadioProps extends ICheckable {
    type: "radio";
    value: string;
    onCheck: (value: string) => void;
    radioGroupValue: string;
}

export interface ICheckboxProps extends ICheckable {
    type: "checkbox";
    onCheck: (isChecked: boolean) => void;
    isChecked: boolean;
}

type ICheckableProps = IRadioProps | ICheckboxProps;


let checkableCounter = 0;


/**
 * @author SBF & AGI
 *
 * @description
 * DON'T USE AS FORM FIELD! Use either Checkbox or Radio instead.
 */
export function Checkable({ children, ...props }: React.PropsWithChildren<ICheckableProps>): JSX.Element {
    const {
        type,
        name,
        value,
        id,
        label,
        className,
        compact,
        disabled,
        autoFocus,
        autoFocusDelay
    } = props;

    const isChecked = props.type === "checkbox" ? props.isChecked : props.value === props.radioGroupValue;

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = props.type === "checkbox"
        ? (e): void => props.onCheck(e.target.checked)
        : (): void => props.onCheck(props.value);

    const [usedID] = useState(id || `${type}-${checkableCounter += 1}`);

    const inputElement = useRef<HTMLInputElement | null>(null);

    const useDynamicEffect = isBrowser ? useLayoutEffect : (): void => {/* Do nothing. */};

    useDynamicEffect(() => {
        if (autoFocus) {
            if (autoFocusDelay) {
                setTimeout(() => inputElement.current && inputElement.current.focus(), autoFocusDelay);
            } else {
                inputElement.current && inputElement.current.focus();
            }
        }
    }, []);

    return (
        <label
            htmlFor={usedID}
            className={`checkable checkable--${type}${compact ? ' checkable--compact' : ''}${disabled ? ' checkable--disabled' : ''} ${className}`.trim()}
            aria-label={label}
            id={`label_${usedID}`}
        >
            <input
                ref={inputElement}
                {...{
                    type,
                    checked: isChecked,
                    onChange: handleChange,
                    name,
                    id: usedID,
                    value: value as string,
                    className: `checkable__field checkable__field--${type}`,
                    "aria-labelledby": `label_${usedID}`,
                    disabled,
                }}
            />
            {
                props.type === "checkbox" ? (
                    <span className="checkable__indicator" aria-hidden>
                        <SvgIcon iconName="checkmark" />
                    </span>
                ) : (
                    <span className="checkable__indicator" aria-hidden />
                )
            }
            <div className="checkable__label">
                {children}
            </div>
        </label>
    );
}
